import React, { useState, useRef, useEffect } from "react";
import SkeletonLoader from "../SkeletonLoader";
import "./HowToScan.scss";

interface HowToScanProps {
  appConfig: any;
  onStartScanning: () => void;
  languageConfig: any;
  clientConfig: any;
  buttonDisableTimeout?: number | null;
}

const HowToScan: React.FC<HowToScanProps> = ({
  appConfig,
  onStartScanning,
  languageConfig,
  clientConfig,
  buttonDisableTimeout = null,
}) => {
  const [videoError, setVideoError] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [currentLanguage, setCurrentLanguage] = useState("en");
  const videoRef = useRef<HTMLVideoElement>(null);

  const howToScanConfig = clientConfig?.howToScanConfig || {};

  // Helper function to get translated text with backward compatibility
  const getTranslatedText = (textObj: any, fallback = "") => {
    // If it's a string (old config format), return as-is
    if (typeof textObj === "string") return textObj;

    // If it's an object (new multilingual format)
    if (typeof textObj === "object" && textObj !== null) {
      return textObj[currentLanguage] || textObj["en"] || fallback;
    }

    // If no value provided, return fallback
    return fallback;
  };

  // Get current language from URL params, default to 'en'
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get("lang");

    // Only set language if it exists in the languageOptions, otherwise default to 'en'
    const splashConfig = clientConfig?.splashScreenConfig || {};
    if (
      langParam &&
      splashConfig?.languageOptions?.some((opt) => opt.value === langParam)
    ) {
      setCurrentLanguage(langParam);
    } else {
      setCurrentLanguage("en"); // Always default to English
    }
  }, [clientConfig?.splashScreenConfig?.languageOptions]);

  // Get configuration with multilingual support
  const howToScanTitle = getTranslatedText(
    howToScanConfig?.howToScanTitle,
    languageConfig?.howToScan || "How to scan?"
  );
  const howToScanVideo = howToScanConfig?.howToScanVideoUrl || "";
  const howToScanDescription = getTranslatedText(
    howToScanConfig?.howToScanDescription,
    languageConfig?.howToScanDescription ||
      "Click on the website link detected to launch the Authenticity Checker"
  );
  const startButtonText = getTranslatedText(
    howToScanConfig?.startButtonText,
    languageConfig?.startScanning || "Start Scanning"
  );
  const backgroundColor = howToScanConfig?.backgroundColor || "#ffffff";
  const primaryColor = howToScanConfig?.primaryColor || "#333333";
  const secondaryColor = howToScanConfig?.secondaryColor || "#FFFFFF";
  const brandLogos = howToScanConfig?.brandLogos || []; // Array of brand logos to display
  const startButton = howToScanConfig?.startButton || {};

  useEffect(() => {
    // Check if all assets are loaded
    const allImagesLoaded =
      brandLogos.length === 0 ||
      brandLogos.every((_, index) => imagesLoaded[index]);
    const videoLoadedOrNotRequired =
      !howToScanVideo || videoLoaded || videoError;
    if (allImagesLoaded && videoLoadedOrNotRequired) {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  }, [
    imagesLoaded,
    videoLoaded,
    videoError,
    brandLogos.length,
    howToScanVideo,
  ]);

  useEffect(() => {
    if (/iP(ad|hone|od)/i.test(navigator.userAgent)) {
      const fallback = setTimeout(() => {
        if (!videoLoaded) {
          setVideoLoaded(true);
        }
      }, 2000);
      return () => clearTimeout(fallback);
    }
  }, [videoLoaded]);

  const handleVideoError = () => {
    console.warn("How to scan video failed to load:", howToScanVideo);
    setVideoError(true);
  };

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  const handleImageLoad = (index: number) => {
    setImagesLoaded((prev) => ({ ...prev, [index]: true }));
  };

  const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement>,
    index: number
  ) => {
    e.currentTarget.style.display = "none";
    setImagesLoaded((prev) => ({ ...prev, [index]: true }));
  };

  // Helper function to get translated logo name
  const getLogoName = (logo: any, index: number) => {
    const logoName = getTranslatedText(logo?.name, `Brand ${index + 1}`);
    return logoName;
  };

  useEffect(() => {
    if (buttonDisableTimeout && buttonDisableTimeout > 0) {
      setIsButtonDisabled(true);

      const timer = setTimeout(() => {
        setIsButtonDisabled(false);
      }, buttonDisableTimeout);

      return () => clearTimeout(timer);
    }
  }, [buttonDisableTimeout]);

  return (
    <div
      className="how-to-scan"
      style={{ backgroundColor }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="how-to-scan-content">
        {isLoading ? (
          // Skeleton loaders
          <>
            {/* Brand Logos Skeleton */}
            {brandLogos.length > 0 && (
              <div className="brand-logos-row" style={{ marginBottom: "20px" }}>
                {brandLogos.map((_, index) => (
                  <div key={index} className="brand-logo-item">
                    <SkeletonLoader
                      width="60px"
                      height="40px"
                      variant="rounded"
                      animation="pulse"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Title Skeleton */}
            <SkeletonLoader
              width="200px"
              height="32px"
              variant="text"
              animation="pulse"
              style={{ marginBottom: "20px" }}
            />

            {/* Video Skeleton */}
            <div className="video-container" style={{ marginBottom: "20px" }}>
              <SkeletonLoader
                width="100%"
                height="200px"
                variant="rounded"
                animation="wave"
              />
            </div>

            {/* Description Skeleton */}
            {/* {howToScanDescription && (
              <SkeletonLoader
                width="80%"
                height="20px"
                variant="text"
                animation="pulse"
                style={{ marginBottom: '20px' }}
              />
            )} */}

            {/* Button Skeleton */}
            <div className="button-container">
              <SkeletonLoader
                width="150px"
                height="45px"
                variant="rounded"
                animation="pulse"
              />
            </div>
          </>
        ) : (
          // Actual content
          <>
            {/* Brand Logos Row */}
            {brandLogos.length > 0 && (
              <div className="brand-logos-row">
                {brandLogos.map((logo, index) => (
                  <div key={index} className="brand-logo-item">
                    <img
                      src={logo.url}
                      alt={getLogoName(logo, index)}
                      className="brand-logo-small"
                      onLoad={() => handleImageLoad(index)}
                      onError={(e) => handleImageError(e, index)}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="how-to-scan-title" style={{ color: primaryColor }}>
              {howToScanTitle}
            </h1>

            {/* Video Container */}
            <div className="video-container">
              {howToScanVideo && !videoError ? (
                <video
                  ref={videoRef}
                  className="how-to-scan-video"
                  autoPlay
                  muted
                  loop
                  playsInline
                  poster=""
                  onError={handleVideoError}
                  onLoadedData={handleVideoLoad}
                >
                  <source src={howToScanVideo} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div
                  className="video-placeholder"
                  style={{ backgroundColor: "#000000" }}
                >
                  {/* Black placeholder screen */}
                </div>
              )}
            </div>

            {/* Description */}
            {/* {howToScanDescription && (
              <p className="how-to-scan-description" style={{ color: primaryColor }}>
                {howToScanDescription}
              </p>
            )} */}

            <div className="button-container" style={{}}>
              <button
                className="start-button"
                // style={{
                //  ...startButton
                // }}
                //onClick={onStartScanning}
                style={{
                  ...startButton,
                  opacity: isButtonDisabled ? 0.4 : 1,
                  cursor: isButtonDisabled ? "not-allowed" : "pointer",
                }}
                onClick={isButtonDisabled ? undefined : onStartScanning}
                disabled={isButtonDisabled}
              >
                {startButtonText}
              </button>
            </div>
          </>
        )}

        {/* Preload images */}
        {brandLogos.map((logo, index) => (
          <img
            key={`preload-${index}`}
            src={logo.url}
            alt=""
            style={{ display: "none" }}
            onLoad={() => handleImageLoad(index)}
            onError={(e) => handleImageError(e, index)}
          />
        ))}

        {/* Preload video */}
        {howToScanVideo && (
          <video
            style={{ display: "none" }}
            onLoadedData={handleVideoLoad}
            onError={handleVideoError}
          >
            <source src={howToScanVideo} type="video/mp4" />
          </video>
        )}
      </div>
    </div>
  );
};

export default HowToScan;
